import React, { useState, useEffect, useRef, useCallback } from "react";
import { FolderPlus, ClipboardEdit } from "lucide-react";
import {
  MenubarContent,
  MenubarItem,
  MenubarSeparator,
} from "@/components/ui/menubar";
import { SavedJourney, Folder } from "@/utils/storage/types";
import RootJourneysList from "./RootJourneysList";
import FoldersList from "./FoldersList";
import MenubarShortcut from "./MenubarShortcut";
import TidyUpDialog from "./TidyUpDialog";
import { Button } from "@/components/ui/button";
import { updateFolder, getFolders, getSavedJourneys } from "@/utils/storage";

interface NotebookMenuContentProps {
  savedJourneys: SavedJourney[];
  folders: Folder[];
  onCreateFolder: () => void;
  onSave: () => void;
  onLoad: (id: string) => void;
  onDelete: (e: React.MouseEvent, id: string) => void;
  onMoveToFolder: (journeyId: string, folderId: string | null) => void;
  openRenameFolder: (e: React.MouseEvent, id: string, name: string) => void;
  handleDeleteFolder: (e: React.MouseEvent, id: string) => void;
  handleSaveToFolder: (folderId: string) => void;
}

const NotebookMenuContent: React.FC<NotebookMenuContentProps> = ({
  savedJourneys: initialSavedJourneys,
  folders: initialFolders,
  onCreateFolder,
  onSave,
  onLoad,
  onDelete,
  onMoveToFolder,
  openRenameFolder,
  handleDeleteFolder,
  handleSaveToFolder,
}) => {
  // Use local state to track data changes and ensure UI stays up-to-date
  const [savedJourneys, setSavedJourneys] = useState(initialSavedJourneys);
  const [folders, setFolders] = useState(initialFolders);
  
  // Update state when props change
  useEffect(() => {
    setSavedJourneys(initialSavedJourneys);
    setFolders(initialFolders);
  }, [initialSavedJourneys, initialFolders]);
  
  const rootJourneys = savedJourneys.filter(journey => !journey.folderId);
  const hasContent = folders.length > 0 || rootJourneys.length > 0;
  
  // Dialog state management
  const [tidyUpDialogOpen, setTidyUpDialogOpen] = useState(false);
  const [activeDialogTab, setActiveDialogTab] = useState<"journeys" | "folders">("journeys");
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Refresh the component data after operations
  const refreshData = useCallback(() => {
    // Refresh from storage to ensure we have the latest data
    try {
      // Get fresh data directly from storage
      const freshJourneys = getSavedJourneys();
      const freshFolders = getFolders();
      
      // Update state with fresh data
      setSavedJourneys([...freshJourneys]);
      setFolders([...freshFolders]);
      
      // Force re-render with a new key
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  }, []);

  // Initialize with fresh data on component mount
  useEffect(() => {
    refreshData();
  }, [refreshData]);
  
  // Simple direct handlers that call the original handlers and refresh data
  const handleDeleteJourney = useCallback((id: string) => {
    // Call the original delete function
    const event = new MouseEvent('click');
    onDelete(event as unknown as React.MouseEvent, id);
    
    // Refresh data after a short delay
    setTimeout(refreshData, 100);
  }, [onDelete, refreshData]);

  const handleFolderDelete = useCallback((id: string) => {
    // Call the original delete function
    const event = new MouseEvent('click');
    handleDeleteFolder(event as unknown as React.MouseEvent, id);
    
    // Refresh data after a short delay
    setTimeout(refreshData, 100);
  }, [handleDeleteFolder, refreshData]);

  const handleRenameFolder = useCallback((id: string, newName: string) => {
    // Use direct folder update function
    updateFolder(id, newName);
    
    // Refresh data after a short delay
    setTimeout(refreshData, 100);
  }, [refreshData]);

  // Handle tab change in dialog
  const handleTabChange = useCallback((tab: string) => {
    setActiveDialogTab(tab as "journeys" | "folders");
  }, []);
  
  // Open dialog handler
  const handleOpenTidyDialog = useCallback(() => {
    refreshData(); // Refresh data before opening
    setTidyUpDialogOpen(true);
  }, [refreshData]);

  // Dialog open state handler - keep it simple
  const handleDialogOpenChange = useCallback((open: boolean) => {
    setTidyUpDialogOpen(open);
    
    // If dialog is closing, refresh the data
    if (!open) {
      setTimeout(refreshData, 100);
    }
  }, [refreshData]);

  return (
    <MenubarContent className="min-w-[260px]" key={refreshKey}>
      <div className="px-2 py-1.5 text-sm font-semibold flex justify-between items-center">
        <span>Journey Map Notebook</span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 flex items-center text-xs text-muted-foreground hover:text-foreground"
          onClick={handleOpenTidyDialog}
        >
          <ClipboardEdit className="h-3 w-3 mr-1" />
          Tidy up
        </Button>
      </div>
      <MenubarSeparator />
      
      <MenubarItem onClick={onSave} className="hover:bg-accent hover:text-accent-foreground">
        Save Current Map
        <MenubarShortcut>⌘S</MenubarShortcut>
      </MenubarItem>
      
      <MenubarItem onClick={onCreateFolder} className="hover:bg-accent hover:text-accent-foreground">
        <FolderPlus className="mr-2 h-4 w-4" />
        Create New Folder
      </MenubarItem>
      
      <RootJourneysList
        journeys={rootJourneys}
        onLoad={onLoad}
        onMove={onMoveToFolder}
        folders={folders}
      />
      
      {!hasContent && (
        <div className="px-2 py-4 text-sm text-muted-foreground text-center">
          No saved journey maps
        </div>
      )}
      
      <FoldersList
        folders={folders}
        savedJourneys={savedJourneys}
        handleSaveToFolder={handleSaveToFolder}
        handleLoad={onLoad}
        handleMoveToFolder={onMoveToFolder}
      />

      <div className="px-2 py-2 text-[10px] text-muted-foreground text-center border-t mt-2">
        All maps are stored locally in your browser. 
        Nothing is saved on our servers.
      </div>

      {/* Re-render TidyUpDialog without using a Fragment */}
      <TidyUpDialog
        key={`tidy-dialog-${refreshKey}`}
        open={tidyUpDialogOpen}
        onOpenChange={handleDialogOpenChange}
        savedJourneys={savedJourneys}
        folders={folders}
        onDeleteJourney={handleDeleteJourney}
        onDeleteFolder={handleFolderDelete}
        onRenameFolder={handleRenameFolder}
        onTabChange={handleTabChange}
        initialTab={activeDialogTab}
      />
    </MenubarContent>
  );
};

export default NotebookMenuContent;
